"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "next/navigation";

import { useAnalytics } from "@context/analytics-context";
import { API_BASE_URL } from "@lib/config";
import { mapProductToItem } from "@lib/analytics/mappers";
import { createBrowserEventId } from "@lib/analytics/event-id";
import {
  claimViewContentPixelEvent,
  resolveViewContentLifecycle,
} from "@lib/analytics/view-content-lifecycle";
import { getVariantIdentifier } from "@lib/util/variants";
import { deliverVisitOnce } from "@lib/analytics/visit-delivery";

type ProductVisitTrackerProps = {
  product?: any;
  productId?: string | null;
  eventId?: string;
};

export default function ProductVisitTracker({
  product,
  productId,
  eventId,
}: ProductVisitTrackerProps) {
  const analytics = useAnalytics();
  const searchParams = useSearchParams();
  const eventIdRef = useRef<{ subjectId: string; eventId: string } | null>(null);
  const requestedVariant = useMemo(() => {
    const requestedId = searchParams?.get("variant")?.trim();
    if (!requestedId || !Array.isArray(product?.variants)) return undefined;
    return product.variants.find(
      (variant: unknown) => getVariantIdentifier(variant) === requestedId,
    );
  }, [product?.variants, searchParams]);
  const requestedVariantId = getVariantIdentifier(requestedVariant);

  const resolveEventId = useCallback(() => {
    const normalizedProductId = String(
      productId || product?._id || product?.id || product?.slug || "product",
    );
    const lifecycleSubjectId = requestedVariantId
      ? `${normalizedProductId}:variant:${requestedVariantId}`
      : normalizedProductId;
    if (eventIdRef.current?.subjectId === lifecycleSubjectId) {
      return eventIdRef.current.eventId;
    }
    if (typeof window !== "undefined") {
      const lifecycle = resolveViewContentLifecycle({
        productId: lifecycleSubjectId,
        providedEventId: eventId,
        historyState: window.history.state,
        createEventId: createBrowserEventId,
      });
      eventIdRef.current = {
        subjectId: lifecycleSubjectId,
        eventId: lifecycle.eventId,
      };
      window.history.replaceState(
        lifecycle.nextHistoryState,
        "",
        window.location.href,
      );
    } else {
      eventIdRef.current = {
        subjectId: lifecycleSubjectId,
        eventId: createBrowserEventId(),
      };
    }
    return eventIdRef.current.eventId;
  }, [eventId, product, productId, requestedVariantId]);

  useEffect(() => {
    if (!analytics.ready || !product) {
      return;
    }
    const id = resolveEventId();
    if (!claimViewContentPixelEvent(id)) {
      return;
    }
    const { item, currency, unitPrice } = mapProductToItem(product, {
      variant: requestedVariant,
    });
    analytics.trackViewItem(
      {
        items: [item],
        currency,
        value: typeof unitPrice === "number" ? unitPrice : undefined,
        contentType:
          !requestedVariant &&
          Array.isArray(product?.variants) &&
          product.variants.length > 0
            ? "product_group"
            : "product",
      },
      {
        eventId: id,
      },
    );
  }, [analytics, product, requestedVariant, resolveEventId]);

  useEffect(() => {
    if (!productId) return;

    const recordVisit = async () => {
      const id = resolveEventId();
      await deliverVisitOnce(`product:${id}`, async () => {
        const referer =
          typeof document !== "undefined" ? document.referrer : undefined;
        const metaSignals = analytics.getMetaSignals() || {
          consent: "unknown" as const,
        };
        return fetch(`${API_BASE_URL}/public/analytics/products/visit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productId,
            variantId: requestedVariantId,
            referer,
            eventId: id,
            eventSourceUrl: metaSignals.eventSourceUrl,
            referrerUrl: metaSignals.referrerUrl,
            consent: metaSignals.consent,
            fbp: metaSignals.fbp,
            fbc: metaSignals.fbc,
            externalId: metaSignals.externalId,
          }),
          keepalive: true,
        });
      });
    };

    void recordVisit();
  }, [analytics, productId, requestedVariantId, resolveEventId]);

  return null;
}
